import React, { useState } from "react";
import { Chip, Tooltip, Typography } from "@mui/material";
import CustomTable from "../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import { useAllTagsViewModel } from "./AllTagsViewModel.js";
import AddTag from "./AddTag.js";
import PublicIcon from "@mui/icons-material/Public";
import { useLoading } from "../../contexts/LoadingContext";
import Modal from "../../components/Modal/Modal";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import CircleIcon from "@mui/icons-material/Circle";
import DeleteIcon from "@mui/icons-material/Delete";
import { useRbac } from "../../hooks/RbacContext";
import { getEnumLabel } from "../../utils/enumUtils.js";


const AllTags = () => {
  const { tags, handleDelete, createTag, updateTag, setSuccessModal, successModal } =
    useAllTagsViewModel();
  const { showLoading, hideLoading } = useLoading();
  const [isAddTagOpen, setIsAddTagOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const { checkAccess } = useRbac();

  const columns = [
    {
      id: "tag_name",
      label: "Tag Name",
      render: (value, row) => {
        const displayText = `${value}`;
        return (
          (
            <Tooltip title={displayText} arrow>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontSize: "inherit",
                  maxWidth: 250,
                  "& a": {
                    color: "inherit",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  },
                }}
              >

                {displayText}

              </Typography>
            </Tooltip>
          )
        );
      },
      sortValue: (value) => `${value}`,
    },
    {
      id: "tag_description",
      label: "Description",
      align: "left",
      render: (value, row) => {
        const displayText = `${value}`;
        return (
          (
            <Tooltip title={displayText} arrow>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontSize: "inherit",
                  maxWidth: 450,
                  "& a": {
                    color: "inherit",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  },
                }}
              >

                {displayText}

              </Typography>
            </Tooltip>
          )
        );
      },
      sortValue: (value, row) => `${value}`,
    },
    {
      id: "tag_type",
      label: "Tag Type",
      render: (value) => getEnumLabel("tag_type", value),
      sortValue: (value) => getEnumLabel("tag_type", value),
    }
  ];

  const fixedColumns = {
    left: [],
    right: ["actions"],
  };

  const handleAddTag = () => {
    setSelectedTag(null);
    setIsAddTagOpen(true);
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setIsAddTagOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddTagOpen(false);
    setSelectedTag(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedTag) {
        await updateTag(selectedTag.tag_id, formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Tag updated successfully",
        });
      } else {
        const addedTag = await createTag(formData);

        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Tag added successfully",
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.log("The error in catch block Tags All is", error.message);

      throw error;
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal(null);
  };

  const handleDeleteClick = (tag) => {
    setDeleteConfirmation({
      tag,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const tag = deleteConfirmation.tag;
    try {
      showLoading();
      await handleDelete(tag);

      setDeleteConfirmation(null);

    } catch (error) {

      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete tag",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (tag) => {
    const actions = [];
    if (checkAccess("tag", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditTag,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    if (checkAccess("tag", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: handleDeleteClick,
        color: "error",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
          },
        },
      });
    }

    return actions;
  };

  const columnFilters = [
    {
      id: "tag_type",
      type: "select",
      label: "Tag Type",
      icon: <PublicIcon />,
      sendIcon: true,
    }
  ];

  return (
    <div>
      <CustomTable
        columns={columns}
        data={tags}
        onAdd={handleAddTag}
        showActions={true}
        showAddButton={checkAccess("tag", "add")}
        addButtonIcon={<PublicIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="All Tags"
        subtitle="Manage and monitor all tags"
        addButtonText="Add Tag"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
      />

      <AddTag
        open={isAddTagOpen}
        onClose={handleCloseDialog}
        initialData={selectedTag}
        onSubmit={handleSubmit}
        tags={tags}
      />
      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={handleCloseSuccessModal}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Tag"
          message={`Are you sure you want to delete tag "${deleteConfirmation.tag.tag_name}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default AllTags;
